"use client";

import { V2_ENDPOINT } from "@/consts";
import { authenticatedFetch, authenticatedFetchJson } from "@/lib/authenticatedFetch";
import type {
  CreateExecutionWebhookRequest,
  CreateExecutionWebhookResponse,
  ExecutionWebhookDeliveriesListResponse,
  ExecutionWebhookDelivery,
  ExecutionWebhookEndpoint,
  ExecutionWebhookListResponse,
} from "@/types/executionWebhook";
import type { OutboundWebhookDelivery } from "@/hooks/useOutboundWebhookDeliveries";
import type { WebhookEvent } from "@/hooks/useWebhookEvents";
import { compactWorkflow } from "./voiceCompact";
import { clampLimit, includesQuery, normalizeText, resolveWorkflow } from "./voiceEntityResolvers";
import { ensureOrgId } from "./voiceToolRuntime";
import { DEFAULT_TOOL_ITEMS, MAX_TOOL_ITEMS, type B3OSVoiceToolsOptions, type Resolution } from "./voiceToolTypes";
import { defineVoiceTool } from "realtime-voice-component";
import { z } from "zod";
import { requireConfirmation } from "./voiceToolRuntime";

type WebhookEventsListResponse = {
  data: {
    items: WebhookEvent[];
    hasMore: boolean;
  };
};

type OutboundWebhookDeliveriesListResponse = {
  data: {
    items: OutboundWebhookDelivery[];
    hasMore: boolean;
  };
};

export async function listExecutionWebhooks(orgId: string) {
  const params = new URLSearchParams({ limit: "100", offset: "0" });
  const response = await authenticatedFetchJson<ExecutionWebhookListResponse>(
    `${V2_ENDPOINT}/v1/execution-webhooks?${params.toString()}`,
    { orgId },
  );
  return response.data.items;
}

export async function listExecutionWebhookDeliveries(orgId: string, endpointId?: string, limit = DEFAULT_TOOL_ITEMS) {
  const params = new URLSearchParams({ limit: String(clampLimit(limit)), offset: "0" });
  if (endpointId) params.set("endpointId", endpointId);
  const response = await authenticatedFetchJson<ExecutionWebhookDeliveriesListResponse>(
    `${V2_ENDPOINT}/v1/execution-webhooks/deliveries?${params.toString()}`,
    { orgId },
  );
  return response.data.items;
}

export async function resolveExecutionWebhookEndpoint(
  orgId: string,
  identifier: string,
): Promise<Resolution<ExecutionWebhookEndpoint>> {
  const endpoints = await listExecutionWebhooks(orgId);
  const query = identifier.trim();
  const matches = endpoints.filter(endpoint =>
    includesQuery([endpoint.id, endpoint.name, endpoint.description, endpoint.url], query),
  );
  const exact = matches.find(
    endpoint =>
      normalizeText(endpoint.id) === normalizeText(query) || normalizeText(endpoint.name) === normalizeText(query),
  );

  if (exact) return { ok: true, item: exact };
  if (matches.length === 1) return { ok: true, item: matches[0] };
  if (matches.length > 1) {
    return {
      ok: false,
      reason: `Multiple execution webhooks matched "${query}".`,
      matches: matches.slice(0, 5),
    };
  }
  return { ok: false, reason: `No execution webhook matched "${query}".` };
}

export async function resolveExecutionWebhookDelivery(
  orgId: string,
  identifier: string,
  endpointId?: string,
): Promise<Resolution<ExecutionWebhookDelivery>> {
  const deliveries = await listExecutionWebhookDeliveries(orgId, endpointId, MAX_TOOL_ITEMS);
  const query = identifier.trim();
  const matches = deliveries.filter(delivery =>
    includesQuery([delivery.id, delivery.endpointId, delivery.workflowId, delivery.runId, delivery.status], query),
  );
  const exact = matches.find(delivery => normalizeText(delivery.id) === normalizeText(query));

  if (exact) return { ok: true, item: exact };
  if (matches.length === 1) return { ok: true, item: matches[0] };
  if (matches.length > 1) {
    return {
      ok: false,
      reason: `Multiple execution webhook deliveries matched "${query}".`,
      matches: matches.slice(0, 5),
    };
  }
  return { ok: false, reason: `No execution webhook delivery matched "${query}".` };
}

async function listInboundWebhookEvents(limit = DEFAULT_TOOL_ITEMS) {
  const params = new URLSearchParams({ limit: String(clampLimit(limit)), offset: "0" });
  const response = await authenticatedFetchJson<WebhookEventsListResponse>(
    `${V2_ENDPOINT}/v1/webhook-events?${params.toString()}`,
  );
  return response.data.items;
}

async function listOutboundWebhookDeliveries(workflowId?: string, limit = DEFAULT_TOOL_ITEMS) {
  const params = new URLSearchParams({ limit: String(clampLimit(limit)), offset: "0" });
  if (workflowId) params.set("workflowId", workflowId);
  const response = await authenticatedFetchJson<OutboundWebhookDeliveriesListResponse>(
    `${V2_ENDPOINT}/v1/outbound-webhook/deliveries?${params.toString()}`,
  );
  return response.data.items;
}

async function resolveInboundWebhookEvent(identifier: string): Promise<Resolution<WebhookEvent>> {
  const events = await listInboundWebhookEvents(MAX_TOOL_ITEMS);
  const query = identifier.trim();
  const matches = events.filter(event =>
    includesQuery([event.id, event.workflowId, event.runId, event.platform, event.status, event.actionTaken], query),
  );
  const exact = matches.find(event => normalizeText(event.id) === normalizeText(query));

  if (exact) return { ok: true, item: exact };
  if (matches.length === 1) return { ok: true, item: matches[0] };
  if (matches.length > 1) {
    return {
      ok: false,
      reason: `Multiple inbound webhook events matched "${query}".`,
      matches: matches.slice(0, 5),
    };
  }
  return { ok: false, reason: `No inbound webhook event matched "${query}".` };
}

async function resolveOutboundWebhookDelivery(
  identifier: string,
  workflowId?: string,
): Promise<Resolution<OutboundWebhookDelivery>> {
  const deliveries = await listOutboundWebhookDeliveries(workflowId, MAX_TOOL_ITEMS);
  const query = identifier.trim();
  const matches = deliveries.filter(delivery =>
    includesQuery(
      [delivery.id, delivery.workflowId, delivery.runId, delivery.nodeId, delivery.url, delivery.status],
      query,
    ),
  );
  const exact = matches.find(delivery => normalizeText(delivery.id) === normalizeText(query));

  if (exact) return { ok: true, item: exact };
  if (matches.length === 1) return { ok: true, item: matches[0] };
  if (matches.length > 1) {
    return {
      ok: false,
      reason: `Multiple outbound webhook deliveries matched "${query}".`,
      matches: matches.slice(0, 5),
    };
  }
  return { ok: false, reason: `No outbound webhook delivery matched "${query}".` };
}

async function postWebhookAction(path: string) {
  const response = await authenticatedFetch(`${V2_ENDPOINT}${path}`, { method: "POST" });
  if (!response.ok) {
    throw new Error(`Webhook action failed (${response.status}).`);
  }
  return response.json().catch(() => null);
}

export function createWebhookVoiceTools(options: B3OSVoiceToolsOptions) {
  return [
    defineVoiceTool({
      name: "list_execution_webhooks",
      description: "List execution webhook endpoints and recent deliveries.",
      parameters: z.object({
        endpoint: z.string().optional(),
        includeDeliveries: z.boolean().optional(),
        limit: z.number().int().positive().max(MAX_TOOL_ITEMS).optional(),
      }),
      execute: async ({ endpoint, includeDeliveries, limit }) => {
        const orgId = ensureOrgId(options);
        let endpointId: string | undefined;
        if (endpoint) {
          const resolved = await resolveExecutionWebhookEndpoint(orgId, endpoint);
          if (!resolved.ok) return resolved;
          endpointId = resolved.item.id;
        }
        const endpoints = await listExecutionWebhooks(orgId);
        const deliveries = includeDeliveries ? await listExecutionWebhookDeliveries(orgId, endpointId, limit) : [];
        return { ok: true, endpoints, deliveries };
      },
    }),
    defineVoiceTool({
      name: "execution_webhook_action",
      description: "Create, enable, disable, retry, or replay execution webhooks/deliveries. Requires confirmation.",
      parameters: z.object({
        action: z.enum(["create", "enable", "disable", "retry_delivery", "replay_delivery"]),
        endpoint: z.string().optional().describe("Endpoint id or name for enable/disable or delivery scoping."),
        delivery: z.string().optional().describe("Delivery id for retry/replay."),
        name: z.string().optional(),
        url: z.string().url().optional(),
        description: z.string().optional(),
        maxRetries: z.number().int().min(0).max(10).optional(),
      }),
      execute: async ({ action, endpoint, delivery, name, url, description, maxRetries }) => {
        const orgId = ensureOrgId(options);

        if (action === "create") {
          if (!name || !url) {
            return { ok: false, reason: "Creating an execution webhook requires name and url." };
          }
          return requireConfirmation(options, {
            action: "execution_webhook_action:create",
            description: `Create execution webhook "${name}"`,
            details: { name, url, description, maxRetries },
            risk: "write",
            title: "Confirm execution webhook creation",
            successMessage: `Created execution webhook ${name}`,
            execute: async () => {
              const request: CreateExecutionWebhookRequest = {
                name,
                url,
                ...(description ? { description } : {}),
                ...(maxRetries !== undefined ? { maxRetries } : {}),
                subscribedEventTypes: ["execution.updated"],
              };
              const response = await authenticatedFetchJson<{ data: CreateExecutionWebhookResponse }>(
                `${V2_ENDPOINT}/v1/execution-webhooks`,
                {
                  method: "POST",
                  headers: { "Content-Type": "application/json" },
                  body: JSON.stringify(request),
                  orgId,
                },
              );
              return { ok: true, action, endpoint: response.data.endpoint, signingSecret: response.data.signingSecret };
            },
          });
        }

        if (action === "enable" || action === "disable") {
          if (!endpoint) return { ok: false, reason: `${action} requires an endpoint id or name.` };
          const resolved = await resolveExecutionWebhookEndpoint(orgId, endpoint);
          if (!resolved.ok) return resolved;
          return requireConfirmation(options, {
            action: `execution_webhook_action:${action}`,
            description: `${action} execution webhook "${resolved.item.name}"`,
            details: { endpoint: resolved.item },
            risk: "write",
            title: "Confirm execution webhook status",
            successMessage: `Completed: ${action} execution webhook ${resolved.item.name}`,
            execute: async () => {
              const response = await authenticatedFetchJson<{ data: ExecutionWebhookEndpoint }>(
                `${V2_ENDPOINT}/v1/execution-webhooks/${resolved.item.id}/${action}`,
                { method: "POST", orgId },
              );
              return { ok: true, action, endpoint: response.data };
            },
          });
        }

        if (!delivery) return { ok: false, reason: `${action} requires a delivery id.` };
        let endpointId: string | undefined;
        if (endpoint) {
          const resolvedEndpoint = await resolveExecutionWebhookEndpoint(orgId, endpoint);
          if (!resolvedEndpoint.ok) return resolvedEndpoint;
          endpointId = resolvedEndpoint.item.id;
        }
        const resolvedDelivery = await resolveExecutionWebhookDelivery(orgId, delivery, endpointId);
        if (!resolvedDelivery.ok) return resolvedDelivery;
        const deliveryAction = action === "retry_delivery" ? "retry" : "replay";

        return requireConfirmation(options, {
          action: `execution_webhook_action:${action}`,
          description: `${deliveryAction} execution webhook delivery ${resolvedDelivery.item.id}`,
          details: { delivery: resolvedDelivery.item },
          risk: "write",
          title: "Confirm execution webhook delivery action",
          successMessage: `Completed: ${deliveryAction} execution webhook delivery`,
          execute: async () => {
            const response = await authenticatedFetchJson<{ data: ExecutionWebhookDelivery }>(
              `${V2_ENDPOINT}/v1/execution-webhooks/deliveries/${resolvedDelivery.item.id}/${deliveryAction}`,
              { method: "POST", orgId },
            );
            return { ok: true, action, delivery: response.data };
          },
        });
      },
    }),
    defineVoiceTool({
      name: "list_webhook_activity",
      description: "List generic inbound webhook events and outbound webhook deliveries.",
      parameters: z.object({
        direction: z.enum(["inbound", "outbound", "all"]).optional(),
        workflow: z.string().optional().describe("Optional workflow id or name to filter outbound deliveries."),
        limit: z.number().int().positive().max(MAX_TOOL_ITEMS).optional(),
      }),
      execute: async ({ direction = "all", workflow, limit }) => {
        let workflowId: string | undefined;
        let workflowSummary: unknown;
        if (workflow) {
          const resolvedWorkflow = await resolveWorkflow(workflow);
          if (!resolvedWorkflow.ok) return resolvedWorkflow;
          workflowId = resolvedWorkflow.item.id;
          workflowSummary = compactWorkflow(resolvedWorkflow.item);
        }

        const [events, deliveries] = await Promise.all([
          direction === "outbound" ? Promise.resolve([]) : listInboundWebhookEvents(limit),
          direction === "inbound" ? Promise.resolve([]) : listOutboundWebhookDeliveries(workflowId, limit),
        ]);

        return { ok: true, workflow: workflowSummary, events, deliveries };
      },
    }),
    defineVoiceTool({
      name: "webhook_activity_action",
      description: "Replay inbound webhook events or retry/replay outbound webhook deliveries. Requires confirmation.",
      parameters: z.object({
        action: z.enum(["replay_inbound_event", "retry_outbound_delivery", "replay_outbound_delivery"]),
        event: z.string().optional().describe("Inbound webhook event id for replay_inbound_event."),
        delivery: z.string().optional().describe("Outbound delivery id for retry/replay."),
        workflow: z.string().optional().describe("Optional workflow id or name to scope outbound delivery lookup."),
      }),
      execute: async ({ action, event, delivery, workflow }) => {
        if (action === "replay_inbound_event") {
          if (!event) return { ok: false, reason: "replay_inbound_event requires an event id." };
          const resolved = await resolveInboundWebhookEvent(event);
          if (!resolved.ok) return resolved;
          return requireConfirmation(options, {
            action,
            description: `Replay inbound webhook event ${resolved.item.id}`,
            details: { event: resolved.item },
            risk: "write",
            title: "Confirm webhook replay",
            successMessage: "Inbound webhook event replay queued",
            execute: async () => {
              const result = await postWebhookAction(`/v1/webhook-events/${resolved.item.id}/replay`);
              return { ok: true, action, event: resolved.item, result };
            },
          });
        }

        if (!delivery) return { ok: false, reason: `${action} requires a delivery id.` };
        let workflowId: string | undefined;
        if (workflow) {
          const resolvedWorkflow = await resolveWorkflow(workflow);
          if (!resolvedWorkflow.ok) return resolvedWorkflow;
          workflowId = resolvedWorkflow.item.id;
        }

        const resolved = await resolveOutboundWebhookDelivery(delivery, workflowId);
        if (!resolved.ok) return resolved;
        const endpointAction = action === "retry_outbound_delivery" ? "retry" : "replay";

        return requireConfirmation(options, {
          action,
          description: `${endpointAction} outbound webhook delivery ${resolved.item.id}`,
          details: { delivery: resolved.item },
          risk: "write",
          title: "Confirm webhook delivery action",
          successMessage: `Outbound webhook delivery ${endpointAction} queued`,
          execute: async () => {
            const result = await postWebhookAction(
              `/v1/outbound-webhook/deliveries/${resolved.item.id}/${endpointAction}`,
            );
            return { ok: true, action, delivery: resolved.item, result };
          },
        });
      },
    }),
  ];
}
